export const meta = {
  id: '25-numerals-1',
  order: 25,
  titleFi: 'Lukusanat 1',
  titleEn: 'Numerals 1',
  duolingoUnit: 'duo-30-numerals-1',
  lemmaIds: ['mēre', 'lanta', 'hāre', 'izula', 'tōma', 'bȳre', 'sīkuda', 'jēnqa', 'vōre', 'ampa', 'mēre-ampā', 'lanta-ampā', 'hāre-ampā', 'izula-ampā', 'tōma-ampā', 'bȳre-ampā', 'sīkuda-ampā', 'jēnqa-ampā', 'vōre-ampā', 'lantēpsa', 'hārēpsa', 'izulēpsa', 'tōmēpsa', 'bȳrēpsa', 'sīkudēpsa', 'jēnqēpsa', 'vōrēpsa', 'se', 'gār', 'langār', 'hārgār', 'zūgār', 'tōngār', 'bȳrgār', 'sīgār', 'jēngār', 'vōrgār', 'pyrys', 'naena', 'ours:sīkudion', 'ours:pyrāzma', 'ours:miliardi', 'ours:prosenti'],
  exampleIds: [
    'ex-643e4c3b8bde',
    'hotd-s1e08-001',
    'ex-fbcbe1390bdd',
    'ex-ff94886202cb',
    'ex-6a699d4a0aec',
    'ex-3aa809b20f73',
    'ex-979b5e85bd99',
    'ex-1c18df49c668',
    'ex-352224a895b7',
    'got-s4e04-020',
    'ex-1cb9240d9061',
    'ex-79357b6f98e5',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Numerals_1',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_numerals',
    'https://dothraki.com/2013/08/valyrian-numerals/',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Compound_numbers',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Number',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['numerals'],
  showLines: ['hotd-s1e08-001', 'got-s4e04-020'],
  pronunciation: { focus: 'stress', words: ['izula', 'sīkuda', 'lantēpsa', 'hārēpsa', 'izulēpsa'], lines: ['got-s4e04-020'] },
  letter: {
    fi: 'Kirjoita sanoin ikäsi ja montako viikkoa on Halloweeniin.',
    en: 'Write in words your age and how many weeks are left until Halloween.',
  },
  scene: 'hotd-s1e08-1',
}

# Lukusanat 1

Valyria laskee kymmenjärjestelmässä kuten suomikin: numerot ovat nollasta yhdeksään, ja uusi numeropaikka alkaa jokaisesta kymmenen potenssista (10, 100, 1 000). Laskutapa on siis tuttu, kielioppi ei. Valyrian perusluvut ovat määritteitä, mutta osa niistä taipuu substantiivin mukaan ja osa ei taivu lainkaan – ja tuhat on kokonaan oma lukunsa. Käydään ne läpi järjestyksessä.

## 1–9: tavallisia adjektiiveja

Luvut yhdestä yhdeksään ovat **tavallisia adjektiiveja**. Ne mukautuvat substantiiviinsa sijassa, suvussa ja luvussa. Yksikön ja monikon valinta on yleensä yksinkertainen: **yksi on yksikössä, kahdesta yhdeksään monikossa.** Petersonin mukaan lukusana on kuitenkin samassa luvussa kuin substantiivi. Aidon <Term id="muutama">muutaman</Term> kanssa myös yksi on monikossa: <Hv glossed>mēriar lentun</Hv> ’yksi yhteisö’. <Term id="kollektiivi">Kollektiivin</Term> kanssa yksi on yksikössä: <Hv glossed>mēre mentyr</Hv> ’yksi armeija’.

Perusmuodot (kuusuvun yksikkö, kuten sanakirjassa):

- 1 <Hv l="mēre">mēre</Hv> (<Term id="adjektiiviluokka">II-luokka</Term>) · 2 <Hv l="lanta">lanta</Hv> (I) · 3 <Hv>hāre</Hv> (II)
- 4 <Hv>izula</Hv> (I) · 5 <Hv l="tōma">tōma</Hv> (I) · 6 <Hv>bȳre</Hv> (II)
- 7 <Hv>sīkuda</Hv> (I) · 8 <Hv>jēnqa</Hv> (I) · 9 <Hv>vōre</Hv> (II)

Luku tulee lähes aina substantiivin **edelle**, ja substantiivi on **monikossa** heti, kun luku on suurempi kuin yksi. Duolingon Tips & Notes antaa sarjan: <Hv glossed>mēre zaldrīzes</Hv> ’yksi lohikäärme’, <Hv glossed>lantyz zaldrīzesse</Hv> ’kaksi lohikäärmettä’, <Hv glossed>hāri zaldrīzesse</Hv> ’kolme lohikäärmettä’.

Tässä suomi johtaa harhaan. Meillä luvun perässä on yksikön partitiivi: *kaksi lohikäärmettä*. Valyriassa substantiivi on monikossa ja siinä sijassa, jota lause vaatii, ja luku taipuu sen mukana.

<Compare fi="lohikäärme|ttä" hv="zaldrīz|esse" label="yksikön partitiivi ~ monikon nominatiivi" />

Muista myös suku. <Hv l="lanta">Lantyz</Hv> on aurinkosuvun muoto, koska <Hv>zaldrīzes</Hv> on aurinkosukua; kuusukuisen sanan kanssa sama luku olisi <Hv l="lanta">lanti</Hv>. Suomessa luvuilla ei ole sukuja, joten tämä on opeteltava.

<Paradigm lemma="lanta" filter="positive.pre" cases="nom,acc" numbers="sg,pl" />

<Example id="ex-643e4c3b8bde" />

<Line id="hotd-s1e08-001" />

Syraxin kolme munaa ovat <Hv glossed>hāriar drōma</Hv>: <Hv glossed>drōmon</Hv> ’muna’ on maasukua, ja luku <Hv>hāre</Hv> on maasuvun monikossa.

## 10–19: ampa ei taivu

Kymmenen on <Hv glossed>ampa</Hv>. Se on adjektiivi, mutta se **ei taivu lainkaan** – ei sijassa, suvussa eikä luvussa.

Luvut 11–19 muodostetaan näin: ensin **taivutettu** luku 1–9, sitten <Hv>ampa</Hv> rinnastusmuodossa <Hv>ampā</Hv>. Rinnastusmuoto on sama pidennetty loppuvokaali, jolla valyria yhdistää sanoja ilman ja-sanaa (<Hv glossed>vala ābrā</Hv> ’mies ja nainen’). Neljätoista on siis kirjaimellisesti *neljä ja kymmenen*.

- 11 <Hv glossed>mēre ampā</Hv> · 12 <Hv glossed>lanta ampā</Hv> · 13 <Hv glossed>hāre ampā</Hv> · 14 <Hv glossed>izula ampā</Hv>
- 15 <Hv glossed>tōma ampā</Hv> · 16 <Hv glossed>bȳre ampā</Hv> · 17 <Hv glossed>sīkuda ampā</Hv> · 18 <Hv glossed>jēnqa ampā</Hv> · 19 <Hv glossed>vōre ampā</Hv>

Tässä suomesta on yllättävän paljon apua. Muistatko, miten *neljätoista* taipuu? *Neljällätoista*, *neljälletoista* – vain alkuosa taipuu, ja *-toista* pysyy aina samana. Valyriassa käy samoin: alkuosa taipuu, <Hv>ampā</Hv> ei koskaan.

<Example id="ex-fbcbe1390bdd" />

Vielä yksi yllätys: luvussa 11 sana <Hv l="mēre">mēre</Hv> on **monikossa**, <Hv glossed>mēri ampā</Hv>, koska yksitoista ei ole enää yksi.

<Example id="ex-ff94886202cb" />

<Example id="ex-6a699d4a0aec" />

Tässä <Hv glossed>ampa</Hv> ’kymmenen’ on taipumaton, mutta luvun <Hv fi="seitsemäntoista">sīkudī ampā</Hv> alkuosa taipuu: <Hv>sīkudī</Hv> on monikon akkusatiivi, koska tytöt ovat rakastamisen kohde.

## 20–99: kymmenet eivät taivu

Tasakymmenet toimivat kuten <Hv>ampa</Hv>: niillä on omat sanansa, ja ne ovat **taipumattomia**:

- 20 <Hv>lantēpsa</Hv> · 30 <Hv>hārēpsa</Hv> · 40 <Hv>izulēpsa</Hv> · 50 <Hv>tōmēpsa</Hv>
- 60 <Hv>bȳrēpsa</Hv> · 70 <Hv>sīkudēpsa</Hv> · 80 <Hv>jēnqēpsa</Hv> · 90 <Hv>vōrēpsa</Hv>

Väliin jäävät luvut muodostetaan kuten 11–19: ensin taipuva luku 1–9, sitten kymmenluku rinnastusmuodossa. 21 on <Hv glossed>mēre lantēpsā</Hv>, 22 <Hv glossed>lanta lantēpsā</Hv> ja niin edelleen. Mukautumisen säännöt ovat samat, joten myös luvussa 21 sana <Hv l="mēre">mēre</Hv> on monikossa: <Hv fi="kaksikymmentäyksi">mēri lantēpsā</Hv>.

<Pronounce word="lantēpsa" />

<Pronounce word="vōrēpsa" />

Kymmenluvuissa paino on aina samassa kohdassa, pitkällä ē:llä: lan·TĒ·psa, vō·RĒ·psa.

<Example id="ex-3aa809b20f73" />

### Ikä: montako vuotta olet nähnyt

Virallisissa esimerkeissä ikä kerrotaan perfektillä: *olen nähnyt kolmekymmentäkuusi vuotta*. Suomalaiselle kielikuva on tuttu runokielestä: *hän on nähnyt monta kesää*.

<Example id="ex-979b5e85bd99" />

<Note>
Tässä luku on yksikössä, vaikka kuusi on monta: <Hv>bȳre</Hv> eikä monikkomuoto. Syy on sanassa <Hv l="jēdar-2" fi="vuodet">jēdari</Hv>. Se on sanan <Hv glossed>jēda</Hv> ’vuosi’ **kollektiivi** – vuodet yhtenä kokonaisuutena – akkusatiivissa. Wikin kieliopin mukaan adjektiivit ja verbit kohtelevat kollektiivia kuin yksikköä, joten luku on yksikön muodossa. Kollektiiviin palaamme <LessonLink n={28}>oppitunnilla 28</LessonLink>.
</Note>

## 100–999: se yhdistää

Sadoilla on taas omat, taipumattomat sanansa:

- 100 <Hv>gār</Hv> · 200 <Hv>langār</Hv> · 300 <Hv>hārgār</Hv> · 400 <Hv>zūgār</Hv> · 500 <Hv>tōngār</Hv>
- 600 <Hv>bȳrgār</Hv> · 700 <Hv>sīgār</Hv> · 800 <Hv>jēngār</Hv> · 900 <Hv>vōrgār</Hv>

Kun satojen perään tulee vielä pienempi luku, sen voi liittää kahdella tavalla. Petersonin omassa esimerkissä loppuluku tulee suoraan perään: <Hv glossed>gār mēri lantēpsā</Hv> ’121’. Duolingon ohjeissa väliin tulee sana <Hv glossed>se</Hv> ’ja’ ja sen jälkeen koko loppuluku sellaisenaan. Satoihin ei kummassakaan lisätä rinnastusmuotoa.

<Interlinear hv="Zūgār se vōr|i tōmēpsā zaldrīz|esse" gloss="neljäsataa ja yhdeksän-NOM.PL viisikymmentä=ja lohikäärme-NOM.PL" fi="neljäsataaviisikymmentäyhdeksän lohikäärmettä" />

<Pronounce word="zūgār" />

<Example id="ex-1c18df49c668" />

<Example id="ex-352224a895b7" />

Kuuntele vielä, miten Daenerys sanoo luvun 163:

<Line id="got-s4e04-020" />

Tässä repliikissä sanaa <Hv l="se">se</Hv> ei ole: on pelkkä <Hv>gār</Hv> ja sen perässä <Hv fi="kuusikymmentäkolme">hāri bȳrēpsā</Hv>, aivan kuten Petersonin esimerkissä 121. Duolingon ohjeiden esimerkissä 459 ja wikin lukusanasivun esimerkissä 410 taas on <Hv l="se">se</Hv>. Kumpikin tapa on virallinen, eikä wiki selitä eroa.

## Tuhat on substantiivi

Tuhat, <Hv glossed>pyrys</Hv>, on **substantiivi** eikä adjektiivi. Se ei määritä substantiivia vaan on itse lausekkeen pääsana: se on siinä sijassa, jota verbi vaatii, ja laskettava substantiivi tulee perään **monikon genetiivissä**: ’tuhat lohikäärmeiden’.

Tämä on suomalaiselle helpompaa kuin luulisi. Mekin sanomme *tuhat lohikäärmettä*: luku on pääsana, ja laskettava on riippuvassa sijassa. Meillä se sija on partitiivi, valyriassa monikon genetiivi.

<Compare fi="lohikäärme|ttä" hv="zaldrīz|oti" label="tuhat + partitiivi ~ pyrys + monikon genetiivi" />

<Pronounce word="pyrys" />

Useammat tuhannet saadaan, kun luku 1–999 määrittää sanaa <Hv>pyrys</Hv>. Duolingon ohjeiden esimerkit: <Hv glossed>pyrys zaldrīzoti</Hv> ’tuhat lohikäärmettä’, <Hv glossed>lantyz jēnqēpsā pyryssy zaldrīzoti</Hv> ’kahdeksankymmentäkaksituhatta lohikäärmettä’. Jälkimmäisessä <Hv l="lanta">lantyz</Hv> mukautuu aurinkosukuisen <Hv>pyrys</Hv>-sanan monikkoon.

<Example id="ex-1cb9240d9061" />

<Interlinear hv="Daenerys ampa pyr|ī Dothrakoti ēza." gloss="Daenerys-NOM kymmenen tuhat-ACC.PL dothraki-GEN.PL omistaa-PRS.3SG" fi="Daenerysilla on kymmenentuhatta dothrakia." />

Kun tuhansien perään tulee vielä pienempi luku, järjestys on wikin lukusanasivun mukaan tämä: ensin tuhannet ilman laskettavaa substantiivia, sitten <Hv l="se">se</Hv> ja tavallinen, luvun määrittämä substantiivi. Virallisessa esimerkissä kaikki on komitatiivissa:

<Example id="ex-79357b6f98e5" />

<Hv glossed>Lantom pyrommi</Hv> on ’kahdentuhannen kanssa’, <Hv glossed>se lantom valommi</Hv> ’ja kahden miehen kanssa’. Kaikki sanat ovat siinä sijassa, jota verbi vaatii.

## Harjoituslauseita

<Practice hv="Lantī valī ūndan." fi="Näin kaksi miestä." />

<Practice hv="Hāri vali ipradis." fi="Kolme miestä syö." />

<Practice hv="Lantēpsa vali ipradis." fi="Kaksikymmentä miestä syö." />

<Practice hv="Ampa ābrī ūndan." fi="Näin kymmenen naista." />

<Practice hv="Pyri valoti ūndan." fi="Näin tuhat miestä." />

## Sanat

<Vocab />

<Check />
